import { useState, useCallback, useMemo, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { Link } from "react-router-dom";
import { MapPin, Home, Maximize2, X, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

// ========== MAP USAGE LIMITER ==========
const MAP_MONTHLY_LIMIT = 10000;
const STORAGE_KEY = "imobi_map_usage";

const getMapUsage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { count: 0, month: new Date().getMonth(), year: new Date().getFullYear() };
    
    const data = JSON.parse(stored);
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // Reset counter if new month
    if (data.month !== currentMonth || data.year !== currentYear) {
      return { count: 0, month: currentMonth, year: currentYear };
    }
    
    return data;
  } catch {
    return { count: 0, month: new Date().getMonth(), year: new Date().getFullYear() };
  }
};

const incrementMapUsage = () => {
  const usage = getMapUsage();
  usage.count += 1;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
  return usage.count;
};

const isMapLimitReached = () => {
  const usage = getMapUsage();
  return usage.count >= MAP_MONTHLY_LIMIT;
};

const getRemainingLoads = () => {
  const usage = getMapUsage();
  return Math.max(0, MAP_MONTHLY_LIMIT - usage.count);
};
// ========================================

const BUCHAREST_CENTER = { lat: 44.4268, lng: 26.1025 };

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
};

// Romanian cities coordinates
const CITY_COORDS = {
  "București": { lat: 44.4268, lng: 26.1025 },
  "Sector 1": { lat: 44.4677, lng: 26.0731 },
  "Sector 2": { lat: 44.4505, lng: 26.1280 },
  "Sector 3": { lat: 44.4169, lng: 26.1585 },
  "Sector 4": { lat: 44.4023, lng: 26.1027 },
  "Sector 5": { lat: 44.4107, lng: 26.0560 },
  "Sector 6": { lat: 44.4340, lng: 26.0130 },
  "Cluj-Napoca": { lat: 46.7712, lng: 23.6236 },
  "Timișoara": { lat: 45.7489, lng: 21.2087 },
  "Iași": { lat: 47.1585, lng: 27.6014 },
  "Constanța": { lat: 44.1598, lng: 28.6348 },
  "Craiova": { lat: 44.3302, lng: 23.7949 },
  "Brașov": { lat: 45.6427, lng: 25.5887 },
  "Galați": { lat: 45.4353, lng: 28.0080 },
  "Ploiești": { lat: 44.9365, lng: 26.0254 },
  "Oradea": { lat: 47.0458, lng: 21.9189 },
  "Sibiu": { lat: 45.7983, lng: 24.1256 },
  "Arad": { lat: 46.1754, lng: 21.3195 },
  "Pitești": { lat: 44.8565, lng: 24.8692 },
  "Bacău": { lat: 46.5670, lng: 26.9146 },
};

// Get coordinates from city name
const getCityCoords = (city) => {
  if (!city) return null;
  
  // Check exact match first
  if (CITY_COORDS[city]) {
    return CITY_COORDS[city];
  }
  
  // Check if city contains known location
  for (const [name, coords] of Object.entries(CITY_COORDS)) {
    if (city.toLowerCase().includes(name.toLowerCase())) {
      return coords;
    }
  }
  
  // Default to Bucharest with slight random offset for variety
  return {
    lat: BUCHAREST_CENTER.lat + (Math.random() - 0.5) * 0.1,
    lng: BUCHAREST_CENTER.lng + (Math.random() - 0.5) * 0.1,
  };
};

const formatPrice = (price, currency = "EUR") => {
  return new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency: currency,
    maximumFractionDigits: 0,
  }).format(price);
};

const PropertyMapView = ({ listings = [], height = "400px", showFullscreenButton = true }) => {
  const [selectedListing, setSelectedListing] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [remainingLoads, setRemainingLoads] = useState(getRemainingLoads());
  const [mapLoaded, setMapLoaded] = useState(false);

  // Check limit on mount
  useEffect(() => {
    if (isMapLimitReached()) {
      setLimitReached(true);
    }
  }, []);

  // Track map load
  useEffect(() => {
    if (mapLoaded) return;
    
    if (!limitReached) {
      const newCount = incrementMapUsage();
      setRemainingLoads(MAP_MONTHLY_LIMIT - newCount);
      setMapLoaded(true);
      
      if (newCount >= MAP_MONTHLY_LIMIT) {
        setLimitReached(true);
      }
    }
  }, [limitReached, mapLoaded]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: limitReached ? "" : (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""),
  });

  // Calculate markers with coordinates
  const markers = useMemo(() => {
    return listings
      .map((listing) => {
        const coords = getCityCoords(listing.city || listing.zone);
        if (!coords) return null;
        
        return {
          ...listing,
          position: {
            lat: coords.lat + (Math.random() - 0.5) * 0.02, // Small random offset to avoid overlapping
            lng: coords.lng + (Math.random() - 0.5) * 0.02,
          },
        };
      })
      .filter(Boolean);
  }, [listings]);

  // Calculate map center based on markers
  const center = useMemo(() => {
    if (markers.length === 0) return BUCHAREST_CENTER;
    
    const avgLat = markers.reduce((sum, m) => sum + m.position.lat, 0) / markers.length;
    const avgLng = markers.reduce((sum, m) => sum + m.position.lng, 0) / markers.length;
    
    return { lat: avgLat, lng: avgLng };
  }, [markers]);

  const onMarkerClick = useCallback((listing) => {
    setSelectedListing(listing);
  }, []);

  const onInfoWindowClose = useCallback(() => {
    setSelectedListing(null);
  }, []);

  // Show limit reached message
  if (limitReached) {
    return (
      <div 
        className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center"
        style={{ height }}
      >
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
        <h3 className="font-semibold text-amber-800 text-lg mb-2">
          Limită lunară atinsă
        </h3>
        <p className="text-amber-700 mb-3">
          S-au folosit toate cele 10.000 de încărcări ale hărții pentru această lună.
        </p>
        <p className="text-amber-600 text-sm">
          Harta va fi disponibilă din nou luna viitoare.
        </p>
        <p className="text-amber-500 text-xs mt-4">
          Folosește vizualizarea "Listă" pentru a vedea proprietățile.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-center">
        <MapPin className="w-8 h-8 mx-auto mb-2 text-red-400" />
        <p>Eroare la încărcarea hărții</p>
        <p className="text-sm text-red-500">Verifică cheia API Google Maps</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div 
        className="bg-stone-100 rounded-xl flex items-center justify-center animate-pulse"
        style={{ height }}
      >
        <div className="text-center text-stone-500">
          <MapPin className="w-10 h-10 mx-auto mb-2 animate-bounce" />
          <p>Se încarcă harta...</p>
        </div>
      </div>
    );
  }

  const mapHeight = isFullscreen ? "100vh" : height;

  return (
    <div 
      className={`relative rounded-xl overflow-hidden ${isFullscreen ? "fixed inset-0 z-50" : ""}`}
      style={{ height: mapHeight }}
    >
      {/* Fullscreen toggle */}
      {showFullscreenButton && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white shadow-md"
        >
          {isFullscreen ? (
            <>
              <X className="w-4 h-4 mr-1" />
              Închide
            </>
          ) : (
            <>
              <Maximize2 className="w-4 h-4 mr-1" />
              Mărește
            </>
          )}
        </Button>
      )}

      {/* Listings count badge */}
      <Badge className="absolute top-3 left-3 z-10 bg-white/90 text-stone-700 shadow-md">
        <MapPin className="w-3 h-3 mr-1" />
        {markers.length} proprietăți
      </Badge>

      {/* Usage counter - show warning when approaching limit */}
      {remainingLoads < 2000 && (
        <Badge 
          className={`absolute top-12 left-3 z-10 shadow-md ${
            remainingLoads < 500 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
          }`}
        >
          <AlertTriangle className="w-3 h-3 mr-1" />
          {remainingLoads.toLocaleString()} încărcări rămase
        </Badge>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={markers.length === 1 ? 14 : 11}
        options={mapOptions}
      >
        {markers.map((listing) => (
          <Marker
            key={listing.id}
            position={listing.position}
            onClick={() => onMarkerClick(listing)}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 50" width="40" height="50">
                  <path d="M20 0C9 0 0 9 0 20c0 15 20 30 20 30s20-15 20-30C40 9 31 0 20 0z" fill="#059669"/>
                  <circle cx="20" cy="18" r="8" fill="white"/>
                  <text x="20" y="22" text-anchor="middle" font-size="10" font-weight="bold" fill="#059669">€</text>
                </svg>
              `)}`,
              scaledSize: new window.google.maps.Size(36, 45),
              anchor: new window.google.maps.Point(18, 45),
            }}
          />
        ))}

        {selectedListing && (
          <InfoWindow
            position={selectedListing.position}
            onCloseClick={onInfoWindowClose}
          >
            <div className="p-1 min-w-[200px] max-w-[280px]">
              {selectedListing.images?.[0] && (
                <img 
                  src={selectedListing.images[0]}
                  alt={selectedListing.title}
                  className="w-full h-24 object-cover rounded-lg mb-2"
                />
              )}
              <h3 className="font-semibold text-stone-900 text-sm line-clamp-2 mb-1">
                {selectedListing.title}
              </h3>
              <p className="text-emerald-600 font-bold text-lg mb-1">
                {formatPrice(selectedListing.price, selectedListing.currency)}
              </p>
              <div className="flex items-center gap-2 text-stone-500 text-xs mb-2">
                {selectedListing.rooms && (
                  <span>{selectedListing.rooms} camere</span>
                )}
                {selectedListing.surface && (
                  <span>• {selectedListing.surface} mp</span>
                )}
              </div>
              <Link 
                to={`/proprietate/${selectedListing.id}`}
                className="block text-center bg-emerald-600 hover:bg-emerald-700 text-white text-sm py-1.5 px-3 rounded-lg transition-colors"
              >
                Vezi detalii
              </Link>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default PropertyMapView;
